<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>

        <div class="d-flex flex-column">

            <b-card
                class="rounded-lg shadow-sm flex-grow-1"
                :class="tableCardClass"
                no-body
            >

                <b-table
                    :items="files"
                    primary-key="id"
                    :fields="fields"
                    show-empty
                    :busy="loading"
                    :per-page="perPage"
                    :current-page="currentPage"
                    @sort-changed="onSortChanged"
                    class="no-top-border cells-align-middle mb-0 base-s3-file-list-table"
                    :class="tableClass"
                >
                    <template #empty>
                        <div class="my-4">
                            <p class="mb-0 text-muted text-center text-uppercase">No Files To Show</p>
                        </div>
                    </template>
                    <template #cell(file_extension)="data">
                        <span v-b-tooltip.hover.d500 :title="data.value" :class="'fiv-sqo fiv-icon-' + data.value" class="font-size-150"></span>
                    </template>
                    <template #cell(filename)="data">
                        <a :href="data.item.download_url" target="_blank">{{ data.item.title ? data.item.title : data.value }}</a>
                        <div v-if="data.item.title" class="text-muted small">{{ data.value }}</div>
                    </template>
                    <template #cell(actions)="data">
                        <b-button
                            v-if="data.item.can_delete"
                            variant="subtle-danger"
                            :disabled="disabled"
                            @click="startDelete(data.item)"
                        >
                            <b-icon icon="trash" class=""></b-icon>
                        </b-button>
                    </template>
                </b-table>

                <template #footer v-if="showFooter">
                    <div class="d-flex justify-content-between align-items-center">
                        <div>Showing {{ startingItem }} - {{ endingItem }} of {{ files.length }} files</div>
                        <div>
                            <b-pagination
                                v-model="currentPage"
                                :total-rows="files.length"
                                :per-page="perPage"
                                size="sm"
                                class="my-0"
                            ></b-pagination>
                        </div>
                    </div>
                </template>

            </b-card>

            <div class="px-3 text-center my-3">
                <b-button
                    v-if="showAdd"
                    variant="primary"
                    class="rounded-pill px-4"
                    style=""
                    :disabled="disabled"
                    @click="addFile"
                >
                    <b-icon icon="cloud-arrow-up-fill" class="mr-2"></b-icon>
                    Add File
                </b-button>
            </div>

        </div>

        <base-s-3-file-modal
            v-if="showAdd"
            ref="addFileModal"
            :file-category="fileCategory"
            :parent-id="parentId"
            @hide="getFiles"
        ></base-s-3-file-modal>

    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import BaseS3FileModal from './BaseS3FileModal';
import { FormatFileSizeHelper, FormatDateHelper } from '../../js/filters-helpers';

export default {
    name: 'BaseS3FileList',
    components: {
        BaseS3FileModal,
    },
    props: {
        listEndpoint: {  // endpoint to get list of files, must use with parentId
            // example: 'item.getFiles'
            // should be something-dot-something -- you'll split it here
            type: String,
            required: true,
        },
        parentId: {  // used for getting list, and for uploading new files
            type: [String, Number],
            required: true,
        },
        fileCategory: {  // pass through to add form
            type: String,
            required: true,
        },
        tableClass: {  // passed on to class of the table
            type: String,
            required: false,
        },
        tableCardClass: {  // class on the b-card around the table -- good for shadows
            type: String,
            required: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        noAdd: {
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            files: [],
            fields: [
                {
                    key: 'file_extension',
                    label: 'Type',
                    class: 'text-center',
                    sortable: true,
                },
                {
                    key: 'filename',
                    label: 'File Name',
                    sortable: true,
                },
                {
                    key: 'filesize',
                    label: 'Size',
                    formatter: value => this.formatFileSize(value),
                    sortable: true,
                },
                {
                    key: 'created',
                    label: 'Uploaded',
                    formatter: value => this.formatDate(value),
                    sortable: true,
                },
                {
                    key: 'actions',
                    label: 'Actions',
                    class: 'text-center',
                }
            ],
            // pagination stuff
            currentPage: 1,
            perPage: 10,

        }
    },
    computed: {
        startingItem: function() {
            return (this.currentPage * this.perPage) - this.perPage + 1;
        },
        endingItem: function() {
            let maxItem = (this.currentPage * this.perPage);
            let totalItems = this.files.length;
            return Math.min(maxItem, totalItems);
        },
        showFooter: function() {
            // only show footer if its paginated (more total items than perPage)
            return this.files.length > this.perPage;
        },
        showAdd: function() {
            return !this.noAdd;
        },
    },
    mounted: function() {
        this.getFiles();
    },
    methods: {
        // call .addFile() from parent to start add process
        //------------------
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        getFiles: function() {
            this.loading = true;
            let endpointSplit = this.listEndpoint.split('.');
            let endpointName = endpointSplit[0];
            let methodName = endpointSplit[1];
            return suppliosApi[endpointName][methodName](this.parentId.toString())
                .then(response => {
                    this.files = response.data;
                    return true;
                })
                .catch(err => {
                    this.handleError(err, 'Error while getting files');
                    return false;
                })
                .then(success => {
                    this.loading = false;
                    return success;
                })
        },
        addFile: function() {
            this.$refs.addFileModal?.startAdd();
        },
        formatFileSize: function(value) {
            return FormatFileSizeHelper(value);
        },
        formatDate: function(value) {
            return FormatDateHelper(value);
        },
        startDelete: function(fileObj) {
            // console.log(fileObj);
            this.$bvModal.msgBoxConfirm(`Are you sure you want to delete ${fileObj.filename}?`, {
                title: 'Delete File?',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Yes, Delete',
                cancelTitle: 'No, Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true,
            })
            .then(value => {
                if (value) {
                    this.performDelete(fileObj);
                }
            });
        },
        performDelete: function(fileObj) {
            this.loading = true;
            return suppliosApi.files.deleteS3Upload(this.fileCategory, fileObj.id)
                .then(response => {
                    console.log(response.data);
                    return true;
                })
                .catch(err => {
                    this.handleError(err, 'Error while deleting file');
                    return false;
                })
                .then(success => {
                    if (success) {
                        this.getFiles();
                        // no need to set loading = false here... will happen in getFiles
                    }
                    else {
                        this.loading = false;
                    }
                    return success;
                });
        },
        onSortChanged: function() {
            this.currentPage = 1;
        },
    }
}
</script>

<style>

</style>